// 1:1 wrappers around the Withings `dropshipment` v2 service. programid is read
// per-call via getProgramId(). ws() forces appname to the app global ('wrpm');
// dropshipment treats appname as caller-ID only (auth = sessionid+programid+userid).

import { ws } from '@/lib/wiservice';
import { getProgramId } from '@/config/program';
import { normalizeCohortCatalog } from '@/logic/devices';
import type { CatalogItem, DropshipmentProduct, PackagingRule } from '@/logic/devices';

export interface AddressSuggestion {
  text: string;
  id: string;
}

export interface OrderAddress {
  address1: string;
  address2: string;
  city: string;
  country: string;
  state: string;
  zip: string;
}

// Both feed lists (single products by EAN + partner-ref kits) collapse into one
// normalized CatalogItem[] here, so no consumer above deals with the duality.
export async function getProductsForCohort(
  cohortid: string | number,
): Promise<CatalogItem[]> {
  const res = await ws<{ products?: DropshipmentProduct[]; packaging_rules?: PackagingRule[] }>(
    'dropshipment',
    'getproductsforcohort',
    { cohortid },
  );
  return normalizeCohortCatalog(res.body ?? {});
}

export async function addressFind(query: string): Promise<AddressSuggestion[]> {
  const res = await ws<{ addresses?: AddressSuggestion[] }>(
    'dropshipment',
    'addressfind',
    { programid: getProgramId(), address: query, limit: 5 },
  );
  return res.body?.addresses ?? [];
}

// Resolves on success (ws() throws WsError on any non-zero platform status).
// A partner ref must ride in `partner_refs` — the platform rejects one passed inside
// `products`. The wiservice layer JSON-encodes the arrays + order_address.
export async function createOrderForPatient(args: {
  userid: string | number;
  products?: string[];
  partnerRefs?: string[];
  orderAddress: OrderAddress;
}): Promise<unknown> {
  const res = await ws(
    'dropshipment',
    'createorderforpatient',
    {
      userid: args.userid,
      programid: getProgramId(),
      ...(args.products?.length && { products: args.products }),
      ...(args.partnerRefs?.length && { partner_refs: args.partnerRefs }),
      order_address: args.orderAddress,
    },
  );
  return res.body;
}

export interface OrderProduct {
  ean?: string;
  model?: number;
  id?: number;
  type?: number;
  partner_ref?: string;
  name?: string;
}

export interface DeviceOrder {
  order_reference: string;
  status: string;
  created: number;
  products: OrderProduct[];
  address?: {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    name?: string;
    email?: string;
    phone_number?: string | null;
  };
  tracking_number?: string | null;
  tracking_url?: string | null;
  parcel_status?: string | null;
  delivery_date?: number | null;
}

export interface AddressVerifyResult {
  valid: boolean;
  errors: string[];
}

// Validate a shipping address before placing an order. Status stays 0 even when
// invalid — the verdict is in body.errors / body.successes. Our single address is
// index 0, so valid = successes includes 0.
export async function verifyAddress(orderAddress: OrderAddress): Promise<AddressVerifyResult> {
  const res = await ws<{ errors?: string[]; successes?: number[] }>(
    'dropshipment',
    'verifyaddress',
    { programid: getProgramId(), addresses: [orderAddress] },
  );
  const errors = res.body?.errors ?? [];
  const successes = res.body?.successes ?? [];
  return { valid: successes.includes(0), errors };
}

// Shipment history for a patient — the `programorder` service (NOT dropshipment).
// Refetched after a successful order (the create mutation invalidates this key).
export async function listOrders(userid: string | number): Promise<DeviceOrder[]> {
  const res = await ws<{ program_orders?: DeviceOrder[] }>(
    'programorder',
    'list',
    { userid, programid: getProgramId() },
  );
  return res.body?.program_orders ?? [];
}
